import type { SkuOption } from "@/api/product-api";

type SolidColor = { type: "solid"; className: string };
type DualColor = { type: "dual"; classes: [string, string] };
type PatternColor = { type: "pattern"; id: "camo" };

type ColorConfig = SolidColor | DualColor | PatternColor;

/**
 * 모든 제품의 색상 옵션을 한곳에서 관리합니다.
 * 색상 추가/변경 시 이곳만 수정하면 됩니다.
 */
export const PRODUCT_COLOR_CONFIG: Record<string, ColorConfig> = {
  // TactSuit 계열
  onyx: { type: "dual", classes: ["bg-black", "bg-red-60"] },
  ash: { type: "dual", classes: ["bg-black", "bg-white"] },
  camo: { type: "pattern", id: "camo" },
  // TactGlove 계열
  black: { type: "solid", className: "bg-[#000000]" },
  charcoal: { type: "solid", className: "bg-[#697077]" },
  brown: { type: "solid", className: "bg-[#8E6A00]" },
  // 악세서리/뱃지 계열
  gold: { type: "solid", className: "bg-[#C9A94E]" },
  silver: { type: "solid", className: "bg-[#C0C0C0]" },
};

const COLOR_PRIORITY: Record<string, number> = {
  ash: 0,
  onyx: 1,
  camo: 2,
  black: 0,
  charcoal: 1,
  brown: 2,
};

/**
 * SKU 옵션이 특정 색상에 해당하는지.
 *
 * 판정 기준은 **`options` 배열**(대소문자 무시)이다 — `sku` 문자열이 아니다.
 * 색상 판정이 필요한 곳은 전부 이 함수를 써야 한다. 예전에 `ColorOptions` 의
 * 썸네일 조회만 `sku.includes(color)` 로 따로 구현돼 있었는데, 필드도 다르고
 * 소문자화도 빠져 있어 두 판정이 갈릴 수 있었다(색상 칩은 뜨는데 썸네일은
 * 못 찾는 식).
 */
export const skuOptionHasColor = (opt: SkuOption, color: string) => {
  // 인자도 소문자화한다 — `getAvailableColors` 는 항상 소문자 `knownColors` 를 넘겨서
  // 안 해도 됐지만, `[slug].tsx` 의 `selectedSku` 가 **검증 없는 URL 입력**
  // (`?color=CAMO`)을 그대로 먹인다. 그러면 매칭이 실패해 `selectedSku` 가 ""가 되고
  // 갤러리가 엉뚱한 이미지 세트로 폴백한다.
  const target = color.toLowerCase();
  return opt.options.some((option) => option.toLowerCase().includes(target));
};

/**
 * skuOptions 에서 사용 가능한 색상 목록을 추출합니다.
 */
export function getAvailableColors(
  skuOptions: SkuOption[],
  knownColors: string[] = Object.keys(PRODUCT_COLOR_CONFIG),
) {
  // 색상당 한 번만 훑는다 — 존재 여부·대표 옵션·재고를 같은 매칭 결과에서 파생시켜야
  // 셋이 어긋날 수 없다(예전엔 filter/find/some 이 각자 술어를 다시 평가했다).
  return knownColors
    .map((color) => ({
      color,
      matches: skuOptions.filter((opt) => skuOptionHasColor(opt, color)),
    }))
    .filter(({ matches }) => matches.length > 0)
    .map(({ color, matches }) => ({
      color,
      name: matches[0].name ?? color,
      available: matches.some((opt) => opt.stock > 0),
    }))
    .sort(
      (a, b) =>
        (COLOR_PRIORITY[a.color] ?? Infinity) -
        (COLOR_PRIORITY[b.color] ?? Infinity),
    );
}
